import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

export const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();

  const helpItems = [
    {
      icon: "business",
      title: "Multi-Level COA System",
      description:
        "Switch between Organization and Branch COAs using the selector. Organization COAs show consolidated accounts while Branch COAs show accounts specific to each location.",
    },
    {
      icon: "visibility",
      title: "Viewing Accounts",
      description:
        "Click on any account node to view detailed information including account code, type, balance, and hierarchy level.",
    },
    {
      icon: "add_circle_outline",
      title: "Adding New Accounts",
      description:
        "Use the dashed placeholder nodes at the beginning and end of each level to add new accounts. The form will automatically set the correct level and parent account.",
    },
    {
      icon: "account_tree",
      title: "Hierarchy Levels",
      description:
        "Level 1 contains your main account categories. Level 2+ contains sub-accounts that belong to parent accounts. Each level represents the depth of your account hierarchy.",
    },
    {
      icon: "color_lens",
      title: "Account Type Colors",
      description:
        "Each account type has a distinct color: Assets (Green), Liabilities (Red), Equity (Blue), Revenue (Purple), Expenses (Orange).",
    },
    {
      icon: "add",
      title: "Adding Sub-Accounts",
      description:
        "Click the + button on any account node to add a sub-account directly under that account. The new account will be created at the next level.",
    },
    {
      icon: "tab",
      title: "Account Type Tabs",
      description:
        "Switch between tabs to view accounts by type. Each tab shows only accounts of that specific type for better organization.",
    },
    {
      icon: "zoom_in",
      title: "Navigation",
      description:
        "Use the minimap in the bottom-right corner to navigate large charts. You can also zoom and pan to explore the hierarchy.",
    },
    {
      icon: "edit",
      title: "Editing Accounts",
      description:
        "Click 'Edit Account' in the details dialog to modify account information. Note that opening balances cannot be changed after creation.",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 1,
        }}
      >
        <Icon
          sx={{
            color: "info.main",
            fontSize: 28,
          }}
        >
          help
        </Icon>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Chart of Accounts Help
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Learn how to use the Chart of Accounts effectively
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <List sx={{ py: 0 }}>
          {helpItems.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem sx={{ px: 0, py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 18, color: "info.main" }}>
                      {item.icon}
                    </Icon>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.5 }}
                    >
                      {item.description}
                    </Typography>
                  }
                />
              </ListItem>
              {index < helpItems.length - 1 && <Divider sx={{ mx: 4 }} />}
            </React.Fragment>
          ))}
        </List>

        {/* Additional Tips Section */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.05),
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "warning.main" }}
          >
            💡 Pro Tips
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            • Use Organization COA for consolidated reporting across all
            branches
            <br />
            • Use Branch COAs for location-specific account management and
            reporting
            <br />
            • Keep account hierarchies consistent between Organization and
            Branch levels
            <br />
            • Use descriptive account codes that reflect the account's purpose
            and location
            <br />
            • Regularly review and clean up unused accounts to maintain clean
            charts
            <br />• Consider the impact on financial reports when restructuring
            accounts
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};
